import { useState } from 'react'
import { Ban, RotateCcw } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Button, Drawer, DrawerSection, KeyValueGrid, StatusBadge } from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import {
  enrollmentOriginLabels,
  formatMembersDate,
  formatMembersDateTime,
  getAccessStatusPresentation,
  getTimelinePresentation,
  revocationReasonLabels,
} from '../model/members-area.presentation'
import { memberDetailQuery } from '../model/members-access.queries'
import { useRestoreGrant, useRevokeGrant } from '../model/members-access.mutations'
import type { MemberDetail, MemberGrant } from '../model/members-access.contract'
import { DeleteConfirmDialog } from './DeleteConfirmDialog'
import {
  drawerActions,
  drawerStack,
  primary,
  secondary,
  timeline,
  timelineBody,
  timelineIcon,
  timelineItem,
} from './members-area.css'

export function StudentDetailDrawer({
  memberId,
  onClose,
}: {
  memberId: string | null
  onClose: () => void
}) {
  const detail = useQuery(memberDetailQuery(memberId ?? ''))
  const revoke = useRevokeGrant()
  const restore = useRestoreGrant()
  const [revokeGrant, setRevokeGrant] = useState<MemberGrant | null>(null)

  if (!memberId) return null

  const member = detail.data
  const status = member ? getAccessStatusPresentation(member.accessStatus) : null
  const StatusIcon = status?.icon

  async function revokeSelected() {
    if (!revokeGrant) return
    try {
      await revoke.mutateAsync(revokeGrant.id)
      toast.success({ title: 'Acesso revogado' })
      setRevokeGrant(null)
    } catch {
      toast.error({ title: 'Não foi possível revogar o acesso' })
    }
  }

  async function restoreSelected(grant: MemberGrant) {
    try {
      await restore.mutateAsync(grant.id)
      toast.success({ title: 'Acesso restaurado' })
    } catch {
      toast.error({ title: 'Não foi possível restaurar o acesso' })
    }
  }

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        title={member?.name ?? 'Membro'}
        description={member?.email}
        eyebrow="Membro"
        status={
          status && StatusIcon ? (
            <StatusBadge
              tone={status.tone}
              label={status.label}
              icon={<StatusIcon size={13} />}
              size="sm"
            />
          ) : undefined
        }
        size="standard"
        variant="detail"
        footer={
          member ? (
            <MemberActions
              member={member}
              onRevoke={setRevokeGrant}
              onRestore={(grant) => {
                void restoreSelected(grant)
              }}
              pending={revoke.isPending || restore.isPending}
            />
          ) : undefined
        }
      >
        <div className={drawerStack} data-testid="members-student-drawer">
          {detail.isError ? (
            <p className={secondary}>Não foi possível carregar o membro.</p>
          ) : !member ? (
            <p className={secondary}>Carregando membro...</p>
          ) : (
            <>
              <DrawerSection title="Concessões">
                <div className={drawerStack}>
                  {member.grants.map((grant) => {
                    const grantStatus = getAccessStatusPresentation(grant.status)
                    return (
                      <KeyValueGrid
                        key={grant.id}
                        items={[
                          { label: 'Produto', value: grant.productName },
                          { label: 'Origem', value: enrollmentOriginLabels[grant.origin] },
                          { label: 'Status', value: grantStatus.label },
                          {
                            label: 'Motivo',
                            value: grant.revocationReason
                              ? revocationReasonLabels[grant.revocationReason]
                              : 'Nenhum',
                          },
                          {
                            label: 'Validade',
                            value: grant.accessUntil
                              ? formatMembersDate(grant.accessUntil)
                              : 'Sem data de término',
                          },
                        ]}
                      />
                    )
                  })}
                </div>
              </DrawerSection>

              <DrawerSection
                title="Histórico do acesso"
                description="Liberações e bloqueios acontecem automaticamente a partir das vendas."
              >
                <ol className={timeline}>
                  {member.timeline.map((event) => {
                    const presentation = getTimelinePresentation(event.kind)
                    const Icon = presentation.icon
                    return (
                      <li key={event.id} className={timelineItem}>
                        <span className={timelineIcon({ tone: presentation.tone })}>
                          <Icon size={15} aria-hidden />
                        </span>
                        <span className={timelineBody}>
                          <span className={primary}>{event.description}</span>
                          <span className={secondary}>
                            {formatMembersDateTime(event.occurredAt)}
                          </span>
                        </span>
                      </li>
                    )
                  })}
                </ol>
              </DrawerSection>
            </>
          )}
        </div>
      </Drawer>

      <DeleteConfirmDialog
        open={revokeGrant !== null}
        title="Revogar acesso"
        description={`Revogar o acesso de ${member?.name ?? 'este membro'} a ${revokeGrant?.productName ?? 'este produto'}?`}
        confirmLabel="Revogar acesso"
        pendingLabel="Revogando..."
        pending={revoke.isPending}
        onCancel={() => setRevokeGrant(null)}
        onConfirm={() => void revokeSelected()}
      />
    </>
  )
}

function MemberActions({
  member,
  onRevoke,
  onRestore,
  pending,
}: {
  member: MemberDetail
  onRevoke: (grant: MemberGrant) => void
  onRestore: (grant: MemberGrant) => void
  pending: boolean
}) {
  const revocable = member.grants.filter((grant) => grant.canRevoke)
  const restorable = member.grants.filter((grant) => grant.canRestore)
  return (
    <div className={drawerActions}>
      {restorable[0] ? (
        <Button
          leftIcon={<RotateCcw size={16} />}
          disabled={pending}
          onClick={() => onRestore(restorable[0]!)}
        >
          Restaurar
        </Button>
      ) : null}
      {revocable[0] ? (
        <Button
          variant="danger"
          leftIcon={<Ban size={16} />}
          disabled={pending}
          onClick={() => onRevoke(revocable[0]!)}
        >
          Revogar acesso
        </Button>
      ) : null}
    </div>
  )
}
